import { useEffect, useState } from "react";
import {
  Autocomplete,
  Button,
  Checkbox,
  Grid,
  CircularProgress,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { downloadRetentionReport } from "../../services/PaymentOrderService";
import { searchClients } from "../../services/ClientService";
import CardHeader from "../General/CardHeader";
import GlassCard from "../General/GlassCard";
import { useToast } from "../../context/ToastContext";

const uncheckedIcon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const parseBlobErrorMessage = async (error, fallback) => {
  try {
    const data = error?.response?.data;
    if (data instanceof Blob) {
      const text = await data.text();
      const json = JSON.parse(text);
      return json?.message || fallback;
    }
    return data?.message || fallback;
  } catch {
    return fallback;
  }
};

export default function RetentionReport() {
  const { showToast } = useToast();
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedClients, setSelectedClients] = useState([]);
  const [onlyPositiveRetentions, setOnlyPositiveRetentions] = useState(true);
  const [loading, setLoading] = useState(false);

  const [clientOptions, setClientOptions] = useState([]);
  const [clientInput, setClientInput] = useState("");
  const [clientsLoading, setClientsLoading] = useState(false);

  useEffect(() => {
    setClientsLoading(true);
    const timeout = setTimeout(async () => {
      try {
        const res = await searchClients({
          search: clientInput.trim(),
          perPage: 30,
        });
        setClientOptions(res.data?.data || []);
      } catch {
        setClientOptions([]);
      } finally {
        setClientsLoading(false);
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [clientInput]);

  const handleGenerateReport = async () => {
    if (!startDate || !endDate) {
      showToast("Debe completar ambas fechas para continuar.", "error");
      return;
    }
    if (startDate.isAfter(endDate)) {
      showToast(
        "La fecha de inicio no puede ser posterior a la fecha de fin.",
        "error",
      );
      return;
    }

    setLoading(true);

    try {
      const payload = {
        start_date: startDate.format("YYYY-MM-DD"),
        end_date: endDate.format("YYYY-MM-DD"),
        client_ids: selectedClients.map((c) => c.id),
        only_positive_retentions: onlyPositiveRetentions,
      };

      const response = await downloadRetentionReport(payload);

      const blob = new Blob([response.data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;

      const contentDisposition = response.headers["content-disposition"];
      const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
      const filename = filenameMatch
        ? filenameMatch[1]
        : `ordenes_pago_pdf_${startDate.format("YYYYMMDD")}_${endDate.format(
            "YYYYMMDD",
          )}.zip`;

      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      showToast("Reporte generado correctamente.", "success");
    } catch (error) {
      const message = await parseBlobErrorMessage(
        error,
        "Ocurrió un error al generar el reporte.",
      );
      showToast(message, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Grid container justifyContent="center" sx={{ mt: 4 }}>
      <Grid size={{ xs: 12, sm: 12, md: 12, lg: 10 }}>
        <GlassCard>
          <CardHeader
            icon={<PictureAsPdfIcon />}
            title="Reporte de Retenciones"
            description="Genera un ZIP con el comprobante en PDF de cada orden de pago del rango indicado."
          />

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 12, md: 6, lg: 6 }}>
              <DatePicker
                label="Desde (fecha de la orden)"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                format="DD/MM/YYYY"
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 12, md: 6, lg: 6 }}>
              <DatePicker
                label="Hasta (fecha de la orden)"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                format="DD/MM/YYYY"
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={clientOptions}
                value={selectedClients}
                onChange={(_, newValue) => setSelectedClients(newValue)}
                inputValue={clientInput}
                onInputChange={(_, newValue, reason) => {
                  if (reason === "input" || reason === "clear") {
                    setClientInput(newValue);
                  }
                }}
                loading={clientsLoading}
                getOptionLabel={(option) =>
                  option.document
                    ? `${option.name} (${option.document})`
                    : option.name
                }
                isOptionEqualToValue={(option, val) => option.id === val.id}
                renderOption={(props, option, { selected }) => {
                  const { key, ...rest } = props;
                  return (
                    <li key={key} {...rest}>
                      <Checkbox
                        icon={uncheckedIcon}
                        checkedIcon={checkedIcon}
                        checked={selected}
                        size="small"
                        sx={{ mr: 1 }}
                      />
                      {option.document
                        ? `${option.name} (${option.document})`
                        : option.name}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Clientes (opcional)"
                    placeholder="Buscar por nombre o documento (CUIT)"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {clientsLoading ? (
                            <CircularProgress color="inherit" size={16} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              <Typography
                variant="caption"
                sx={{ opacity: 0.65, display: "block", mt: 0.5 }}
              >
                Se buscan las órdenes cuyo CUIT beneficiario coincida con el
                documento de los clientes seleccionados. Sin clientes = todas las
                órdenes del rango.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={onlyPositiveRetentions}
                    onChange={(e) =>
                      setOnlyPositiveRetentions(e.target.checked)
                    }
                  />
                }
                label="Solo órdenes con retenciones positivas"
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={handleGenerateReport}
                disabled={loading}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Generar Reporte"
                )}
              </Button>
            </Grid>
          </Grid>
        </GlassCard>
      </Grid>
    </Grid>
  );
}
